
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Key, ShieldAlert, Loader2 } from 'lucide-react';
import { deleteGalleryItem } from '@/services/galleryService';

interface AdminPanelProps {
  itemId: string;
  onDelete: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ itemId, onDelete }) => {
  const [adminKey, setAdminKey] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { toast } = useToast();

  const handleDelete = async () => {
    if (!adminKey) {
      toast({
        title: "Admin Key Required",
        description: "Please enter the admin key to delete this item.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      const success = await deleteGalleryItem(itemId, adminKey);
      
      if (success) {
        toast({
          title: "Item Deleted",
          description: "The gallery item has been successfully deleted.",
        });
        onDelete();
      } else {
        toast({
          title: "Deletion Failed",
          description: "Could not delete the item. Please check your admin key.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="mt-6">
      {!isExpanded ? (
        <Button 
          variant="outline" 
          size="sm"
          className="border-gray-300 text-gray-500"
          onClick={() => setIsExpanded(true)}
        >
          <ShieldAlert className="mr-2 h-4 w-4" />
          Admin Options
        </Button>
      ) : (
        <Card className="border-red-100 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-red-800 flex items-center">
              <ShieldAlert className="mr-2 h-5 w-5" />
              Admin Panel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-red-800">
                These actions require admin privileges. Please enter your admin key to proceed.
              </p>
              
              <div className="flex items-center gap-2">
                <div className="relative grow">
                  <Key className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    type="password"
                    placeholder="Enter admin key"
                    className="pl-8"
                    value={adminKey}
                    onChange={(e) => setAdminKey(e.target.value)}
                  />
                </div>
                
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={!adminKey || isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Item
                    </>
                  )}
                </Button>
              </div>
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-500"
                onClick={() => setIsExpanded(false)}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminPanel;
